import {
  isAllowedFutureOrUnchangedDate,
  toDateOnlyYmd,
} from "./dateTime";

export const EVENT_NAME_REQUIRED = "Event name is required.";
export const EVENT_DATE_REQUIRED = "Event date is required.";
export const EVENT_DATE_FUTURE = "Event date must be a future date.";
export const EVENT_VENUE_REQUIRED = "Please select a venue.";
export const TICKET_NAME_REQUIRED = "Ticket name is required.";
export const TICKET_PRICE_INVALID = "Ticket price must be greater than 0.";
export const TICKET_QUANTITY_INVALID = "Ticket quantity must be a positive whole number.";
export const TICKET_TIER_REQUIRED = "Add at least one ticket tier.";

export const isPositivePrice = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0;
};

export const isPositiveIntegerQuantity = (value) => {
  const raw = String(value ?? "").trim();
  if (!/^\d+$/.test(raw)) return false;
  return Number(raw) >= 1;
};

export const getTitleError = (title, { allowEmpty = false } = {}) => {
  if (!String(title || "").trim()) {
    return allowEmpty ? "" : EVENT_NAME_REQUIRED;
  }
  return "";
};

export const getDateError = (date, { allowEmpty = false, isEditMode = false, originalEventDate = "" } = {}) => {
  const selectedDate = toDateOnlyYmd(date);
  if (!selectedDate) {
    return allowEmpty ? "" : EVENT_DATE_REQUIRED;
  }
  if (!isAllowedFutureOrUnchangedDate(selectedDate, { originalYmd: isEditMode ? originalEventDate : "" })) {
    return EVENT_DATE_FUTURE;
  }
  return "";
};

export const getVenueError = (venue, { allowEmpty = false } = {}) => {
  if (!String(venue || "").trim()) {
    return allowEmpty ? "" : EVENT_VENUE_REQUIRED;
  }
  return "";
};

export const getTicketNameError = (name, { allowEmpty = false } = {}) => {
  if (!String(name || "").trim()) {
    return allowEmpty ? "" : TICKET_NAME_REQUIRED;
  }
  return "";
};

export const getTicketPriceError = (price, { allowEmpty = false } = {}) => {
  const raw = String(price ?? "").trim();
  if (!raw) {
    return allowEmpty ? "" : TICKET_PRICE_INVALID;
  }
  return isPositivePrice(raw) ? "" : TICKET_PRICE_INVALID;
};

export const getTicketQuantityError = (quantity, { allowEmpty = false } = {}) => {
  const raw = String(quantity ?? "").trim();
  if (!raw) {
    return allowEmpty ? "" : TICKET_QUANTITY_INVALID;
  }
  return isPositiveIntegerQuantity(raw) ? "" : TICKET_QUANTITY_INVALID;
};

export const getTicketSectionError = (section, { allowEmpty = false, label = "ticket tier" } = {}) => {
  if (!String(section || "").trim()) {
    return allowEmpty ? "" : `Select a section for ${label}.`;
  }
  return "";
};

export const validateEventCore = ({
  title,
  date,
  venue,
  isEditMode = false,
  originalEventDate = "",
} = {}) => {
  if (!String(title || "").trim()) {
    return { field: "title", message: EVENT_NAME_REQUIRED };
  }

  const selectedDate = toDateOnlyYmd(date);
  if (!selectedDate) {
    return { field: "date", message: EVENT_DATE_REQUIRED };
  }

  if (!isAllowedFutureOrUnchangedDate(selectedDate, { originalYmd: isEditMode ? originalEventDate : "" })) {
    return { field: "date", message: EVENT_DATE_FUTURE };
  }

  if (!String(venue || "").trim()) {
    return { field: "venue", message: EVENT_VENUE_REQUIRED };
  }

  return null;
};

export const validateTicketTierBasics = (tiers = [], { hasAutoQuantity = () => false } = {}) => {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    return { field: "tickets", message: TICKET_TIER_REQUIRED, index: -1 };
  }

  for (let index = 0; index < tiers.length; index += 1) {
    const tier = tiers[index];

    if (!String(tier?.name || "").trim()) {
      return { field: `ticket-name-${index}`, message: TICKET_NAME_REQUIRED, index };
    }

    if (!isPositivePrice(tier?.price)) {
      return { field: `ticket-price-${index}`, message: TICKET_PRICE_INVALID, index };
    }

    if (!hasAutoQuantity(tier) && !isPositiveIntegerQuantity(tier?.quantity)) {
      return { field: `ticket-qty-${index}`, message: TICKET_QUANTITY_INVALID, index };
    }
  }

  return null;
};
